import i18n from '@/i18n';
import ConfirmStore from '@/store/common/ConfirmStore';
import CustomLabelsStore from '@/store/common/CustomLabelsStore';
import FollowUpFormStore from '@/store/grievance/FollowUpFormStore';
import GrievanceFormStore from '@/store/grievance/GrievanceFormStore';
import GrievanceItemStore from '@/store/grievance/GrievanceItemStore';
import ForgotPasswordStore from '@/store/session/ForgotPasswordStore';
import ResetPasswordStore from '@/store/session/ResetPasswordStore';
import SessionStore from '@/store/session/SessionStore';
import { RootState } from '@/store/types';
import axios from '@/utils/axios';
import { ErrorMessage } from '@/utils/ErrorMessage';
import * as Sentry from '@sentry/browser';
import Vue from 'vue';
import Vuex, { ActionTree, MutationTree, Store } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

Vue.use(Vuex);

export const state: RootState = {
    locale: i18n.locale,
    instanceId: '',
    tenantId: '',
    hasCustomFavIcon: false,
    showAnonymousEmailNotification: true,
    langs: ['en'],
    isLoading: false,
    errorMessages: [],
};

export const mutations: MutationTree<RootState> = {
    setLocale(state, payload) {
        i18n.locale = payload.locale;
        state.locale = payload.locale;
    },
    setConfig(state, payload) {
        state.instanceId = payload.instanceId;
        state.tenantId = payload.tenantId;
        state.hasCustomFavIcon = payload.hasCustomFavIcon;
        state.langs = payload.langs;
        state.locale = payload.locale;
    },
    addErrorMessage(state, payload) {
        state.errorMessages.push(payload.errorMessage);
        Sentry.captureException(payload.errorMessage.error);
        // eslint-disable-next-line no-console
        console.error(payload.errorMessage.error);
    },
    removeErrorMessage(state, payload) {
        const index = state.errorMessages.findIndex(errorMessage => errorMessage.id === payload.errorMessage.id);
        if (index > -1) {
            state.errorMessages.splice(index, 1);
        }
    },
    setIsLoading(state, payload) {
        state.isLoading = payload.isLoading;
    },
    setshowAnonymousEmailNotification(state, payload) {
        state.showAnonymousEmailNotification = payload.data?.grievanceAnonymousNoNotifications?.value ?? true;
    },
    removeAllErrorMessages(state) {
        state.errorMessages = [];
    },
};

export const actions: ActionTree<RootState, RootState> = {
    async initPortal({ commit }, { instanceId }) {
        commit('setIsLoading', { isLoading: true });
        try {
            const initResponse = await axios.get(`${instanceId}/portal_init`);
            const { tenantID, hasCustomFavIcon, langs, instanceTitle } = initResponse.data;
            const availableLangs: string[] = langs.map((l: string) => l.split('_')[0]);
            let locale = i18n.locale;
            if (!availableLangs.includes(locale)) {
                locale = availableLangs.find(lang => lang === 'en') || availableLangs[0];
                i18n.locale = locale;
            }
            commit('setConfig', {
                instanceId,
                tenantId: tenantID,
                hasCustomFavIcon,
                langs: availableLangs,
                locale,
            });
            const configResponse = await axios.get(`${instanceId}/portal_config`);
            commit('setshowAnonymousEmailNotification', configResponse.data);
            commit('customLabels/setCustomLabels', { ...configResponse.data, instanceId, instanceTitle });
        } finally {
            commit('setIsLoading', { isLoading: false });
        }
    },
    addError({ commit }, payload) {
        const errorMessage = new ErrorMessage(
            payload.error,
            payload.error.response?.data?.message ?? payload.message,
            payload.timeout,
            payload.error.response?.data?.showDetails ?? payload.showDetails,
        );
        commit('addErrorMessage', { errorMessage });
        if (errorMessage.timeout > 0) {
            setTimeout(() => {
                commit('removeErrorMessage', { errorMessage });
            }, errorMessage.timeout);
        }
    },
    changeLocale({ commit }, payload) {
        commit('setIsLoading', { isLoading: true });
        setTimeout(() => {
            commit('setLocale', payload);
            window.location.reload();
        });
    },
};

export function rehydrated(store: Store<RootState>) {
    if (store.state.locale) {
        i18n.locale = store.state.locale;
    }
}

export default new Vuex.Store<RootState>({
    state,
    mutations,
    actions,
    modules: {
        session: SessionStore,
        customLabels: CustomLabelsStore,
        grievanceForm: GrievanceFormStore,
        grievanceItem: GrievanceItemStore,
        forgotPassword: ForgotPasswordStore,
        resetPassword: ResetPasswordStore,
        confirm: ConfirmStore,
        followUpForm: FollowUpFormStore,
    },
    plugins: [
        createPersistedState<RootState>({
            paths: ['locale'],
            rehydrated,
        }),
    ],
});
